import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { savedSearchApi } from '../lib/api'
import type { SaveSavedSearchRequest, GenerateListFromSearchRequest } from '../types/savedSearch'
import type { FilterEvaluationRequest } from '../types/contactList'

export function useSavedSearches() {
  return useQuery({
    queryKey: ['saved-searches'],
    queryFn: async () => {
      const response = await savedSearchApi.getAll()
      return response.data
    },
  })
}

export function useSavedSearchDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['saved-searches', id],
    queryFn: async () => {
      if (!id) throw new Error('Saved search ID is required')
      const response = await savedSearchApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateSavedSearch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSavedSearchRequest) =>
      savedSearchApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-searches'] })
    },
  })
}

export function useUpdateSavedSearch(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveSavedSearchRequest) =>
      savedSearchApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['saved-searches', id], data)
      queryClient.invalidateQueries({ queryKey: ['saved-searches'] })
    },
  })
}

export function useDeleteSavedSearch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => savedSearchApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-searches'] })
    },
  })
}

export function useSavedSearchCount(request: FilterEvaluationRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['saved-searches', 'count', request],
    queryFn: async () => {
      if (!request) throw new Error('Request is required')
      const response = await savedSearchApi.getCount(request)
      return response.data
    },
    enabled: enabled && !!request,
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000,
  })
}

export function useSavedSearchPreview(request: FilterEvaluationRequest | null, enabled: boolean) {
  return useQuery({
    queryKey: ['saved-searches', 'preview', request],
    queryFn: async () => {
      if (!request) throw new Error('Request is required')
      const response = await savedSearchApi.getPreview(request)
      return response.data
    },
    enabled: enabled && !!request,
    placeholderData: keepPreviousData,
  })
}

export function useSavedSearchFieldMetadata() {
  return useQuery({
    queryKey: ['saved-searches', 'field-metadata'],
    queryFn: async () => {
      const response = await savedSearchApi.getFieldMetadata()
      return response.data
    },
    staleTime: 30 * 60 * 1000,
  })
}

export function useSavedSearchTags() {
  return useQuery({
    queryKey: ['saved-searches', 'tags'],
    queryFn: async () => {
      const response = await savedSearchApi.getTags()
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useGenerateListFromSearch(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: GenerateListFromSearchRequest) =>
      savedSearchApi.generateList(id, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}
